import { useCallback, useMemo } from "react";
import { isNull } from "lodash";

import toGlobalId from "scenes/Hubly/components/Workspace/api/helper";

import { createResolverFactory, useQuery } from "../../TaskForm/hooks";

function getConditionsFragment(conditionsGroupId) {
  return conditionsGroupId
    ? `fragment ConditionFields on Condition {
                    id
                    operator
                    type
                    name
                    operand
                    sortOrder
                    formField {
                      uuid
                      name
                    }
                    datePart
                    workflowPart
                    value {
                      ... on StringValue {
                        stringValue
                      }
                      ... on ArrayStringValue {
                        arrayStringValue
                      }
                    }
                    tags {
                      edges {
                        node {
                          uuid
                          name
                        }
                      }
                    }
                    streams {
                      edges {
                        node {
                          uuid
                          name
                          color
                        }
                      }
                    }
                  }`
    : "";
}

function getConditionsGroupQuery(conditionsGroupId) {
  return conditionsGroupId
    ? `conditionsGroup(id: "${conditionsGroupId}") {
        id
        conditions {
          edges {
            node {
              ...ConditionFields
              nestedConditionGroup {
                id
                conditions {
                  edges {
                    node {
                      ...ConditionFields
                      nestedConditionGroup {
                        id
                        conditions {
                          edges {
                            node {
                              ...ConditionFields
                              nestedConditionGroup {
                                uuid
                                conditions {
                                  edges {
                                    node {
                                      ...ConditionFields
                                      nestedConditionGroup {
                                        id
                                        conditions {
                                          edges {
                                            node {
                                              ...ConditionFields
                                              nestedConditionGroup {
                                                id
                                                conditions {
                                                  edges {
                                                    node {
                                                      ...ConditionFields
                                                      nestedConditionGroup {
                                                        id
                                                        conditions {
                                                          edges {
                                                            node {
                                                              ...ConditionFields
                                                              nestedConditionGroup {
                                                                id
                                                                conditions {
                                                                  edges {
                                                                    node {
                                                                      ...ConditionFields
                                                                      nestedConditionGroup {
                                                                        id
                                                                        conditions {
                                                                          edges {
                                                                            node {
                                                                              ...ConditionFields
                                                                            }
                                                                          }
                                                                        }
                                                                      }
                                                                    }
                                                                  }
                                                                }
                                                              }
                                                            }
                                                          }
                                                        }
                                                      }
                                                    }
                                                  }
                                                }
                                              }
                                            }
                                          }
                                        }
                                      }
                                    }
                                  }
                                }
                              }
                            }
                          }
                        }
                      }
                    }
                  }
                }
              }
            }
          }
        }
        }`
    : "";
}

function extractValue({
  node: { value, streams, tags, nestedConditionGroup, formField, ...rest },
}) {
  const v = isNull(Object.values(value)[0])
    ? undefined
    : Object.values(value)[0];

  return {
    ...rest,
    nestedConditionGroup: nestedConditionGroup
      ? nestedConditionGroup.conditions.edges.map(extractValue)
      : null,
    value: v,
    streams: streams.edges.map(({ node }) => {
      return { ...node, id: node.uuid };
    }),
    tags: tags.edges.map(({ node }) => {
      return { ...node, id: node.uuid };
    }),
    formField: formField ? { ...formField, id: formField.uuid } : null,
  };
}

export function useConditionQuery(workflowId, cg, order) {
  const factory = useCallback(() => {
    const query = `${getConditionsFragment(cg?.id)}
                  query getConditions($workflowId: ID!, $taskOrder: Int) {
                    conditionTypes(workflowId: $workflowId, taskOrder: $taskOrder) {
                      type
                      name
                      parameters {
                        name
                        id
                        operands
                        options
                        dateParts {
                          name
                          operands
                          options
                          type {
                            id
                            showOptions
                            text
                          }
                        }
                        type {
                          id
                          showOptions
                          text
                        }
                      }
                    }
                    ${getConditionsGroupQuery(cg?.id)}}`;
    const resolver = ({
      data: { conditionTypes = [], conditionsGroup = null },
    }) => {
      return [
        conditionTypes,
        conditionsGroup
          ? {
              ...conditionsGroup,
              conditions: conditionsGroup.conditions.edges.map(extractValue),
            }
          : null,
      ];
    };

    return { query: query, resolver: createResolverFactory(resolver) };
  }, [cg]);

  const {
    loading,
    data: [conditionTypes = [], conditionsGroup = null] = [],
    error,
  } = useQuery(
    factory,
    useMemo(() => {
      return {
        variables: {
          workflowId: toGlobalId("Workflow", workflowId),
          taskOrder: order,
        },
      };
    }, [workflowId, order])
  );

  return {
    loadingQuery: loading,
    conditionTypes: conditionTypes,
    conditionsGroup: conditionsGroup || cg,
    error: error
      ? error[0]?.message ||
        "Failed to load condition data, please retry or contact support for assistance"
      : "",
  };
}

export default useConditionQuery;
